import { themedConfirm } from '../../lib/themed-dialogs';
// fleet-groups-tab.tsx — správa fleet skupin (jen fleet), tenant-scoped.
// Master-detail jako Identity/Mesh: TenantTree vlevo, fleet skupiny + stanice vpravo.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Select,
  StatusDot,
} from '../../components/ui';
import { TenantTree, type TenantFlat } from '../../components/TenantTree';
import { Building2, FolderPlus, Layers, Plus, Server, Trash2, X } from 'lucide-react';

interface Group {
  id: string;
  name: string;
  description?: string;
  tenant_id: string;
  tenant_slug: string;
  agent_count: number;
}
interface Assignment {
  agent_id: string;
  hostname: string;
  os?: string;
  status?: string;
  group_id?: string | null;
  tenant_id?: string | null;
}

export function GroupsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);

  const { data: tenantsData } = useQuery({
    queryKey: ['fl-mesh-tenants'],
    queryFn: () =>
      api.mcp<{ ok: boolean; tenants: TenantFlat[] }>('mesh.tenants', { action: 'list' }),
  });
  const { data: groups = [] } = useQuery({
    queryKey: ['fl-groups'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; groups: Group[] }>('fleet.group_list', {});
      return r.groups ?? [];
    },
  });
  const { data: assignments = [] } = useQuery({
    queryKey: ['fl-assignments'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; assignments: Assignment[] }>(
        'fleet.assignments_list',
        {},
      );
      return r.assignments ?? [];
    },
  });

  const tenants = tenantsData?.tenants ?? [];

  // souhrny pro TenantTree: počet fleet skupin + přiřazených stanic per tenant
  const summaries = useMemo(() => {
    const out: Record<string, { group_count: number; member_count: number; key_count: number }> = {};
    for (const tn of tenants) {
      out[tn.id] = {
        group_count: groups.filter((g) => g.tenant_id === tn.id).length,
        member_count: assignments.filter((a) => a.tenant_id === tn.id && a.group_id).length,
        key_count: 0,
      };
    }
    return out;
  }, [tenants, groups, assignments]);

  const tenant = tenants.find((tn) => tn.id === selectedTenant) ?? null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Levý sloupec — strom tenantů */}
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">
              <Building2 size={14} className="inline mr-1" /> {t('fleetGroups.tenants')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TenantTree
              tenants={tenants}
              selected={selectedTenant}
              onSelect={setSelectedTenant}
              summaries={summaries}
              showRootOrg={false}
            />
          </CardContent>
        </Card>
      </div>

      {/* Pravý sloupec — detail vybraného tenanta */}
      <div className="lg:col-span-2 space-y-4">
        {!tenant
          ? <EmptyState icon={Layers} title={t('fleetGroups.selectTenant')} description={t('fleetGroups.selectTenantDesc')} />
          : (
            <TenantGroupsDetail
              tenant={tenant}
              groups={groups.filter((g) => g.tenant_id === tenant.id)}
              assignments={assignments}
              onChanged={() => {
                qc.invalidateQueries({ queryKey: ['fl-groups'] });
                qc.invalidateQueries({ queryKey: ['fl-assignments'] });
              }}
            />
          )}
      </div>
    </div>
  );
}

function TenantGroupsDetail(
  { tenant, groups, assignments, onChanged }: {
    tenant: TenantFlat;
    groups: Group[];
    assignments: Assignment[];
    onChanged: () => void;
  },
) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const createGroup = useMutation({
    mutationFn: (name: string) =>
      api.mcp('fleet.group_create', { tenant: tenant.slug, name }),
    onSuccess: () => {
      toast.success(t('fleetGroups.groupCreated'));
      setNewName('');
      setCreating(false);
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteGroup = useMutation({
    mutationFn: (id: string) => api.mcp('fleet.group_delete', { group_id: id }),
    onSuccess: () => { toast.success(t('fleetGroups.groupDeleted')); onChanged(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const setGroup = useMutation({
    mutationFn: (b: { agentId: string; group_id: string | null }) =>
      api.mcp('fleet.set_agent_group', {
        agent_id: b.agentId,
        group_id: b.group_id ?? undefined,
      }),
    onSuccess: onChanged,
    onError: (e: Error) => toast.error(e.message),
  });

  const inGroup = (gid: string) => assignments.filter((a) => a.group_id === gid);
  const unassigned = assignments.filter((a) => !a.group_id);

  return (
    <>
      {/* Header */}
      <Card>
        <CardContent className="py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 size={18} className="text-pink-400" />
            <div>
              <span className="text-sm font-bold text-white">{tenant.name}</span>
              <span className="ml-2 text-[10px] text-gray-500 font-mono">{tenant.slug}</span>
            </div>
          </div>
          <div className="flex gap-4 text-[10px] text-gray-500">
            <span><Layers size={11} className="inline" /> {t('fleetGroups.groupsCount', { count: groups.length })}</span>
            <span><Server size={11} className="inline" /> {t('fleetGroups.stationsCount', { count: assignments.filter((a) => a.tenant_id === tenant.id && a.group_id).length })}</span>
          </div>
        </CardContent>
      </Card>

      {/* Skupiny */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-sm">
            <span><Layers size={14} className="inline mr-1" /> {t('fleetGroups.fleetGroupsTitle', { count: groups.length })}</span>
            <Button size="sm" variant="primary" icon={<FolderPlus size={12} />} onClick={() => setCreating(true)}>
              {t('fleetGroups.newGroup')}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {creating && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-gray-900/40 rounded">
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('fleetGroups.groupNamePlaceholder')}
                className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && newName.trim()) createGroup.mutate(newName.trim());
                  if (e.key === 'Escape') { setCreating(false); setNewName(''); }
                }}
              />
              <Button size="sm" variant="primary" onClick={() => newName.trim() && createGroup.mutate(newName.trim())} disabled={createGroup.isPending}>
                {t('fleetGroups.create')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setCreating(false); setNewName(''); }}>{t('common.cancel')}</Button>
            </div>
          )}
          {groups.length === 0 && !creating
            ? <p className="text-xs text-gray-500">{t('fleetGroups.noGroups')}</p>
            : (
              <div className="space-y-1">
                {groups.map((g) => (
                  <div key={g.id}>
                    <div
                      className="flex items-center justify-between p-2 bg-gray-900/40 rounded text-xs cursor-pointer hover:bg-gray-800/60"
                      onClick={() => setOpenGroup(openGroup === g.id ? null : g.id)}
                    >
                      <div className="flex items-center gap-2">
                        <Layers size={13} className="text-cyan-400" />
                        <span className="text-white font-medium">{g.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge>{t('fleetGroups.stationsCount', { count: g.agent_count })}</Badge>
                        <button
                          title={t('fleetGroups.delete')}
                          onClick={async (e) => { e.stopPropagation(); if (await themedConfirm(t('fleetGroups.deleteConfirm', { name: g.name }))) deleteGroup.mutate(g.id); }}
                          className="text-gray-500 hover:text-red-400 p-0.5"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    {openGroup === g.id && (
                      <div className="ml-5 mt-1 mb-2 space-y-1 border-l border-gray-800 pl-3">
                        {inGroup(g.id).length === 0
                          ? <p className="text-[11px] text-gray-600">{t('fleetGroups.noStationsInGroup')}</p>
                          : inGroup(g.id).map((a) => (
                            <div key={a.agent_id} className="flex items-center justify-between text-[11px] py-0.5">
                              <span className="flex items-center gap-1.5">
                                <StatusDot status={a.status === 'online' ? 'ok' : 'idle'} />
                                {a.hostname} <span className="text-gray-600">{a.os}</span>
                              </span>
                              <button title={t('fleetGroups.remove')} onClick={() => setGroup.mutate({ agentId: a.agent_id, group_id: null })} className="text-gray-500 hover:text-red-400">
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        {/* přidat stanici */}
                        {unassigned.length > 0 && (
                          <div className="flex items-center gap-1.5 pt-1">
                            <Plus size={12} className="text-gray-500" />
                            <Select
                              defaultValue=""
                              onChange={(e) => { if (e.target.value) setGroup.mutate({ agentId: e.target.value, group_id: g.id }); }}
                            >
                              <option value="">{t('fleetGroups.addUnassignedStation')}</option>
                              {unassigned.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.hostname}</option>)}
                            </Select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {/* Nepřiřazené stanice */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            <Server size={14} className="inline mr-1" /> {t('fleetGroups.unassignedTitle', { count: unassigned.length })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {unassigned.length === 0
            ? <p className="text-xs text-gray-500">{t('fleetGroups.allAssigned')}</p>
            : (
              <div className="flex flex-wrap gap-1.5">
                {unassigned.map((a) => (
                  <Badge key={a.agent_id} variant={a.status === 'online' ? 'green' : 'gray'}>{a.hostname}</Badge>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
    </>
  );
}
